import type { AuthResponse, CharacterDTO } from "@ghostland/shared";
import * as api from "./api.js";

/**
 * In-memory session. The access token is intentionally NOT persisted (kept out of
 * localStorage to limit XSS exposure); on page load we re-mint it from the httpOnly
 * refresh cookie via `bootstrap()`.
 */
class Session {
  private accessToken: string | null = null;
  hasCharacter = false;
  /** Whether the account's email is confirmed (gates character creation). */
  emailVerified = true;
  /** The signed-in email (when known), shown on the confirmation screen. */
  email: string | null = null;
  /** The active character, populated after load/creation. */
  character: CharacterDTO | null = null;

  get token(): string | null {
    return this.accessToken;
  }

  get isAuthenticated(): boolean {
    return this.accessToken !== null;
  }

  private apply(res: AuthResponse): AuthResponse {
    this.accessToken = res.token;
    this.hasCharacter = res.hasCharacter;
    this.emailVerified = res.emailVerified;
    return res;
  }

  /** Re-send the confirmation email to this (unverified) account. */
  async resendVerification(): Promise<void> {
    if (!this.accessToken) throw new Error("Your session expired — log in again to resend.");
    await api.resendVerification(this.accessToken);
  }

  async register(email: string, password: string, alphaKey: string): Promise<AuthResponse> {
    this.email = email;
    return this.apply(await api.register({ email, password, alphaKey }));
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    this.email = email;
    return this.apply(await api.login({ email, password }));
  }

  /** Attempt to restore a session from the refresh cookie. Returns true if signed in. */
  async bootstrap(): Promise<boolean> {
    try {
      this.apply(await api.refresh());
      return true;
    } catch {
      this.accessToken = null;
      return false;
    }
  }

  async logout(): Promise<void> {
    try {
      await api.logout();
    } finally {
      this.accessToken = null;
      this.hasCharacter = false;
      this.character = null;
      this.email = null;
      this.emailVerified = true;
    }
  }
}

export const session = new Session();
